import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import usePageTracking from "../../hooks/usePageTracking";
import { activityStart, activityEnd } from "../../redux/slices/trackActivitySlice";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import blogService from "../../api/blogService";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import GuidedTour from "../../components/tour/GuidedTour";
import useTour from "../../components/tour/useTour";
import getBlogsTourSteps from "./blogsTourSteps";
import "./blogs.scss";

// ── Blog Card ────────────────────────────────────────────────────
function BlogCard({ post }) {
  const [imgError, setImgError] = useState(false);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const imageUrl = post.featured_image
    ? `${process.env.REACT_APP_API_BASE_URL}/uploads/blog/${post.featured_image}`
    : null;

  return (
    <article className="blog-card">
      <div className="blog-card-image">
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={post.title}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="blog-card-image-fallback">
            <svg
              className="blog-fallback-icon"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="6"
                y="8"
                width="36"
                height="32"
                rx="4"
                fill="rgba(255,255,255,0.2)"
              />
              <rect
                x="12"
                y="16"
                width="24"
                height="3"
                rx="1.5"
                fill="rgba(255,255,255,0.7)"
              />
              <rect
                x="12"
                y="22"
                width="18"
                height="2.5"
                rx="1.25"
                fill="rgba(255,255,255,0.5)"
              />
              <rect
                x="12"
                y="27"
                width="20"
                height="2.5"
                rx="1.25"
                fill="rgba(255,255,255,0.5)"
              />
              <rect
                x="12"
                y="32"
                width="14"
                height="2.5"
                rx="1.25"
                fill="rgba(255,255,255,0.5)"
              />
            </svg>
            <p className="blog-fallback-title">No image available</p>
          </div>
        )}
      </div>
      <div className="blog-card-body">
        {post.categories?.length > 0 && (
          <div className="blog-card-categories">
            {post.categories.map((cat) => (
              <span key={cat.id} className="blog-category-badge">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <h2>{post.title}</h2>
        {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}
        <p className="blog-card-date">
          {formatDate(post.created_at)}
        </p>
      </div>
    </article>
  );
}

// ── Blog Card Skeleton ───────────────────────────────────────────
function BlogCardSkeleton() {
  return (
    <div className="blog-card-skeleton" aria-hidden="true">
      <div className="bsk-image" />
      <div className="bsk-body">
        <div className="bsk bsk--pill" />
        <div className="bsk bsk--title" />
        <div className="bsk bsk--title bsk--title-sm" />
        <div className="bsk bsk--line" />
        <div className="bsk bsk--line bsk--line-md" />
        <div className="bsk bsk--line bsk--line-sm" />
        <div className="bsk bsk--date" />
      </div>
    </div>
  );
}

// ── Main Blogs Page ──────────────────────────────────────────────
export default function Blogs() {
  const dispatch = useDispatch();
  usePageTracking("BLOG");
  useEffect(() => {
    dispatch(activityStart({ page_name: "BLOG", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "BLOG" }));
  }, [dispatch]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category_id") || "";

  // Only restore scroll/data when coming back from a blog detail page
  const isComingFromDetail = location.state?.fromBlogDetail === true;

  // ── State ──────────────────────────────────────────────────────
  const [allBlogs, setAllBlogs] = useState(() => {
    if (!isComingFromDetail) return [];
    const savedCategory = sessionStorage.getItem("blogCategory");
    const savedData = sessionStorage.getItem("blogData");
    if (savedData && savedCategory === categoryId) {
      try {
        const parsed = JSON.parse(savedData);
        return Array.from(new Map(parsed.map((b) => [b.id, b])).values());
      } catch {
        /* ignore */
      }
    }
    return [];
  });

  const [page, setPage] = useState(() => {
    if (!isComingFromDetail) return 1;
    const savedCategory = sessionStorage.getItem("blogCategory");
    const savedPage = sessionStorage.getItem("blogPage");
    if (savedPage && savedCategory === categoryId) {
      return parseInt(savedPage, 10);
    }
    return 1;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const observerTarget = useRef(null);
  const hasRestoredRef = useRef(isComingFromDetail && allBlogs.length > 0);
  const previousScrollHeight = useRef(0);
  const isFetchingRef = useRef(false);
  const prevCategoryRef = useRef(categoryId);

  // Scroll to top on mount — skip only when restoring position from detail
  useEffect(() => {
    if (!isComingFromDetail) {
      window.scrollTo(0, 0);
      sessionStorage.removeItem("blogScrollPosition");
      sessionStorage.removeItem("blogPage");
      sessionStorage.removeItem("blogData");
      sessionStorage.removeItem("blogCategory");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset when category changes ───────────────────────────────
  useEffect(() => {
    if (prevCategoryRef.current === categoryId) return;
    prevCategoryRef.current = categoryId;

    sessionStorage.removeItem("blogScrollPosition");
    sessionStorage.removeItem("blogPage");
    sessionStorage.removeItem("blogData");
    sessionStorage.removeItem("blogCategory");

    setPage(1);
    setAllBlogs([]);
    setError(null);
    hasRestoredRef.current = false;
    window.scrollTo(0, 0);
  }, [categoryId]);

  // ── Restore scroll position after session data loads ──────────
  useEffect(() => {
    if (hasRestoredRef.current && allBlogs.length > 0) {
      const savedScroll = sessionStorage.getItem("blogScrollPosition");
      if (savedScroll) {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedScroll, 10));
          sessionStorage.removeItem("blogScrollPosition");
          sessionStorage.removeItem("blogPage");
          sessionStorage.removeItem("blogData");
          sessionStorage.removeItem("blogCategory");
          hasRestoredRef.current = false;
        });
      }
    }
  }, [allBlogs]);

  // ── Fetch blogs ───────────────────────────────────────────────
  const fetchBlogs = useCallback(async (pageNum, catId) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (pageNum === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
      previousScrollHeight.current = document.documentElement.scrollHeight;
    }
    setError(null);

    try {
      const params = { page: pageNum, limit: 9, sort: "latest" };
      if (catId) params.category_id = catId;

      const res = await blogService.getBlogs(params);
      const data = res.data?.data;
      if (data) {
        const docs = data.docs || [];
        const total = data.total || 0;
        const limit = data.limit || 9;
        setTotalPages(Math.ceil(total / limit));
        setAllBlogs((prev) => {
          if (pageNum === 1) return docs;
          const combined = [...prev, ...docs];
          return Array.from(new Map(combined.map((b) => [b.id, b])).values());
        });
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Trigger fetch on page or category change (skip when restoring session)
  useEffect(() => {
    if (hasRestoredRef.current && allBlogs.length > 0) return;
    fetchBlogs(page, categoryId);
  }, [page, categoryId, fetchBlogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Adjust scroll position after more content is appended
  useEffect(() => {
    if (page > 1 && previousScrollHeight.current > 0) {
      requestAnimationFrame(() => {
        const diff =
          document.documentElement.scrollHeight - previousScrollHeight.current;
        if (diff > 0) window.scrollTo(0, window.scrollY);
        previousScrollHeight.current = 0;
      });
    }
  }, [allBlogs, page]);

  // Save state before navigating away
  useEffect(() => {
    const save = () => {
      if (allBlogs.length > 0) {
        sessionStorage.setItem("blogScrollPosition", window.scrollY.toString());
        sessionStorage.setItem("blogPage", page.toString());
        sessionStorage.setItem("blogData", JSON.stringify(allBlogs));
        sessionStorage.setItem("blogCategory", categoryId);
      }
    };
    const links = document.querySelectorAll('a[href^="/"]');
    links.forEach((link) => link.addEventListener("click", save));
    return () =>
      links.forEach((link) => link.removeEventListener("click", save));
  }, [page, allBlogs, categoryId]);

  // ── Infinite scroll ───────────────────────────────────────────
  const hasMore = page < totalPages;

  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isLoading && !isLoadingMore) {
        setIsLoadingMore(true);
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading, isLoadingMore],
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element || !hasMore) return;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    });
    observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver, hasMore]);

  // ── Derive selected category name from loaded blogs ──────────
  const selectedCategoryName = useMemo(() => {
    if (!categoryId || allBlogs.length === 0) return null;
    for (const blog of allBlogs) {
      const cat = blog.categories?.find((c) => String(c.id) === String(categoryId));
      if (cat) return cat.name;
    }
    return null;
  }, [allBlogs, categoryId]);

  // ── Clear category filter ─────────────────────────────────────
  const handleClearFilter = () => {
    navigate("/blog");
  };

  // Guided tour — a single "Read a post" step, auto-starting on first visit
  // and replayable from the header's tour button (pageKey "blog"). Gated to
  // once the first blog card has loaded so the ring has something to anchor to.
  const { isOpen: isTourOpen, finish: finishTour } = useTour("blog", {
    autoStart: allBlogs.length > 0,
  });

  return (
    <>
    <NoIndexSEO title={seoConfig.blogs.title} />
    <GuidedTour
      isOpen={isTourOpen}
      steps={getBlogsTourSteps()}
      onDone={finishTour}
    />
    <div className="blog-page">
      <div className="blog-container">
        {/* Header */}
        <div className="blog-header">
          <span className="blog-eyebrow">BLOG</span>
          <h1>Insights for nurses who want more</h1>
          <p className="blog-subtitle">
            Salary trends, relocation guides, financial tips, and career advice
            from the MapMyPay team.
          </p>

          {/* Clear category filter button */}
          {categoryId && (
            <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
              {selectedCategoryName && (
                <div className="blog-selected-category">
                  Category: {selectedCategoryName}
                </div>
              )}
              <button
                className="blog-clear-filter-btn"
                onClick={handleClearFilter}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
                Clear filter and show all bloges
              </button>
            </div>
          )}
        </div>

        {/* Loading — skeleton grid */}
        {isLoading && page === 1 && (
          <div className="blog-grid">
            {Array.from({ length: 9 }).map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="blog-state-center">
            <p className="blog-error-text">
              Failed to load blogs. Please try again.
            </p>
          </div>
        )}

        {/* Blog grid */}
        {!isLoading && !error && (
          <>
            <div className="blog-grid">
              {allBlogs.map((post, idx) => (
                <a
                  key={`${post.id}-${post.slug}`}
                  className="blog-card-link"
                  data-tour={idx === 0 ? "bl-card" : undefined}
                  href={`/blog/${post.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    sessionStorage.setItem(
                      "blogScrollPosition",
                      window.scrollY.toString(),
                    );
                    sessionStorage.setItem("blogPage", page.toString());
                    sessionStorage.setItem(
                      "blogData",
                      JSON.stringify(allBlogs),
                    );
                    sessionStorage.setItem("blogCategory", categoryId);
                    navigate(`/blog/${post.slug}`);
                  }}
                >
                  <BlogCard post={post} />
                </a>
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={observerTarget} className="blog-load-more">
                {isLoadingMore && <div className="blog-spinner" />}
              </div>
            )}

            {/* Empty state */}
            {allBlogs.length === 0 && (
              <div className="blog-empty">
                <p>No blogs found{categoryId ? " for this category" : ""}.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
}
